import { Component, DestroyRef, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { combineLatest, takeUntil, distinctUntilChanged, filter, Subject, finalize, firstValueFrom } from 'rxjs';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { VirtualSensor, VirtualSensorsService } from '@services/virtual-sensors.service';
import { DataPoint } from '@services/datapoints-data.service';
import { Account } from '@services/accounts.service';
import { Organisation } from '@services/organisation-data.service';
import { convertEpochToDateString } from '@functions/functions';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { EditVirtualSensorComponent, editVirtualSensorDialogData } from 'src/app/dialogs/edit-virtual-sensor/edit-virtual-sensor.component';
import { MatDialog } from '@angular/material/dialog';
import { AddVirtualSensorComponent, AddVirtualSensorDialogData } from 'src/app/dialogs/add-virtual-sensor/add-virtual-sensor.component';
import { IQueryParameterFiltering, QueryParamType } from '@global/iFiltering';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * FilterQueryParams
 *
 * Used to filter the table of Rules
 */
export interface FilterQueryParams {

	Location?: string;

	Filter?: string;
	pageIndex?: number;
	pageSize?: number;

	SortColumn?: string;
	SortDirection?: SortDirection;

}


@Component({
	selector: 'app-virtual-sensors',
	templateUrl: './virtual-sensors.component.html',
	styleUrl: './virtual-sensors.component.scss'
})
export class VirtualSensorsComponent implements OnInit, OnChanges, IQueryParameterFiltering<FilterQueryParams> {

	/** Observable Subject Finalise */
	private destroyRef = inject(DestroyRef);

	/** Initialised filterQueryParams */
	filterQueryParams: FilterQueryParams = {};


	/**
	 * filterQueryParamTypes
	 *
	 * Definition of the Query Parameter data types
	 */
	filterQueryParamTypes: Record<keyof FilterQueryParams, QueryParamType> = {

		Location: 'string',

		Filter: 'string',
		pageIndex: 'number',
		pageSize: 'number',

		SortColumn: 'string',
		SortDirection: 'string'

	};

	/** AccountGUID */
	public AccountGUID: string;

	/** OrganisationUID */
	public OrganisationUID: string;

	/** Asset */
	@Input() asset: LevelAsset;

	/** currentUser */
	public currentUser: WebAppUser;

	/** account */
	public account: Account;

	/** organisation */
	public organisation: Organisation;

	/** virtualSensors */
	public virtualSensors: VirtualSensor[] = [];

	/** displayedColumns */
	displayedColumns: string[] = ['VirtualSensorName', 'InputDataPoints', 'Conversion', 'Edit', 'Delete'];

	/** datasource */
	dataSource: MatTableDataSource<VirtualSensor>;

	// =========================================================================
	// Paginator and sort configuration
	// =========================================================================

	private _paginatorSensors: MatPaginator;

	@ViewChild('paginatorSensors')
	set paginatorSensors(paginator: MatPaginator) {
		try {
			if (!paginator) { return; }

			if (this.filterQueryParams.pageSize) { paginator.pageSize = this.filterQueryParams.pageSize; }
			if (this.filterQueryParams.pageIndex) { paginator.pageIndex = this.filterQueryParams.pageIndex; }

			this._paginatorSensors = paginator;

			if (this.dataSource) {
				this.dataSource.paginator = paginator;
			}
		} catch (error) {
			console.error('Error assigning Asset paginator:', error);
		}
	}

	private _sortSensors: MatSort;

	@ViewChild('sortSensors')
	set sortSensors(sort: MatSort) {
		try {
			if (!sort) { return; }

			if (this.filterQueryParams.SortColumn) { sort.active = this.filterQueryParams.SortColumn }
			if (this.filterQueryParams.SortDirection) { sort.direction = this.filterQueryParams.SortDirection }

			this._sortSensors = sort;

			if (this.dataSource) {
				this.dataSource.sort = sort;
			}
		} catch (error) {
			console.error('Error assigning Rules sort:', error);
		}
	}

	/** assetVirtualSensors */
	public assetVirtualSensors: VirtualSensor[] = [];

	/**
	 * Default Constructor
	 * @param assetsDataService assetsDataService 
	 * @param globalFunctions globalFunctions 
	 * @param authenticationService authenticationService 
	 * @param siteSubscriptions siteSubscriptions 
	 * @param router router 
	 */
	constructor(
		public assetsDataService: AssetsDataService,
		public globalFunctions: GlobalFunctions,
		public authenticationService: AuthenticationService,
		private siteSubscriptions: siteSubscriptions,
		private router: Router,
		private route: ActivatedRoute,
		private virtualSensorsService: VirtualSensorsService,
		public dialog: MatDialog,
	) {

	}

	// =========================================================================
	// Lifecycle
	// =========================================================================

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {
		combineLatest([
			this.siteSubscriptions.selectedAccountGUID$.pipe(distinctUntilChanged()),
			this.siteSubscriptions.selectedAccount$.pipe(distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisationUID$.pipe(distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisation$.pipe(distinctUntilChanged()),
			this.siteSubscriptions.hierarchicalData$.pipe(distinctUntilChanged()),
			this.authenticationService.currentUser$.pipe(distinctUntilChanged()),
			this.route.queryParamMap.pipe(distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([selectedAccountGUID, account, selectedOrganisationUID, selectedOrganisation, hierarchicalData, currentUser, queryParamMap]) =>
				!!selectedAccountGUID && !!account && !!selectedOrganisationUID
				&& !!selectedOrganisation && !!hierarchicalData && !!currentUser && !!queryParamMap),
			takeUntilDestroyed(this.destroyRef)
		).subscribe({
			next: async ([selectedAccountGUID, account, selectedOrganisationUID, selectedOrganisation, hierarchicalData, currentUser, queryParamMap]) => {

				this.AccountGUID = selectedAccountGUID;
				this.OrganisationUID = selectedOrganisationUID;
				this.currentUser = currentUser;
				this.account = account;
				this.organisation = selectedOrganisation;

				/** Default to look for only open cases */
				this.filterQueryParams = this.globalFunctions.queryParamsToObject<FilterQueryParams>(
					queryParamMap,
					this.filterQueryParamTypes
				);

				await this.getVirtualSensors();

				this.applyQueryParameters("All", false);

			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * ngOnChanges
	 * @param changes changes 
	 */
	ngOnChanges(changes: SimpleChanges): void {
		if (changes['asset'] && !changes['asset'].firstChange) {
			this.getAssetVirtualSensors();
		}
	}

	/**
	 * getVirtualSensors
	 */
	async getVirtualSensors() {

		try {
			const virtualSensors = await firstValueFrom(
				this.virtualSensorsService.getVirtualSensor$(this.AccountGUID, this.OrganisationUID)
					.pipe(
						filter((result) => !!result)
					)
			);

			if (virtualSensors) {
				this.virtualSensors = virtualSensors;
				this.virtualSensors.forEach((vs: VirtualSensor) => {
					vs._dataPoints = JSON.parse(vs.DataPoints ?? '[]').map((x: DataPoint) => (
						{
							value: `[${this.getValueForDP(x, vs)}]`,
							datapoint: x
						}
					));
				});
				if (this.asset) {
					this.getAssetVirtualSensors();
				}
			}
		}
		catch (error: any) {
			if (error.status != 404) {
				this.globalFunctions.HandleError(error, "this.virtualSensorsService.getVirtualSensor$");
			}
		} finally {
			this.assignSensorsToDataSource();
		}

	}

	/**
	 * assignSensorsToDataSource
	 */
	assignSensorsToDataSource() {

		if (!this.dataSource) {
			this.dataSource = new MatTableDataSource(this.virtualSensors);
		}
		else {
			this.dataSource.data = this.virtualSensors;
		}

		this.dataSource.filterPredicate = (sensor: VirtualSensor, filter: string) => {


			const query = filter?.toLowerCase().trim() ?? '';
			if (!query) { return true; }

			// Combine the necessary fields for filtering
			const combinedStr = [
				sensor.VirtualSensorName
			]
				.filter(value => value != null)
				.join(' ')
				.toLowerCase();

			return combinedStr.includes(query)
		};

		this.dataSource.sortingDataAccessor = (item, property) => {
			switch (property) {
				case 'VirtualSensorName': return item.VirtualSensorName || '';
				default: return '';
			}
		};

	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	/**
	 * goToVirtualSensor
	 * @param virtualSensor virtualSensor 
	 */
	goToVirtualSensor(virtualSensor: VirtualSensor) {
		this.router.navigate([`./setup/virtual-datapoint-management/${this.OrganisationUID}/virtual-datapoint/${virtualSensor.VirtualSensorID}`]);
	}

	/**
	 * editVirtualSensor
	 * @param virtualSensor virtualSensor 
	 */
	editVirtualSensor(virtualSensor: VirtualSensor) {
		let dialogData: editVirtualSensorDialogData = {
			virtualSensor: virtualSensor
		}

		const dialogRef = this.dialog.open(EditVirtualSensorComponent, {
			data: dialogData
		});
		dialogRef.afterClosed().pipe(
		).subscribe((result: any) => {
			if (result) {
				virtualSensor.VirtualSensorName = result.virtualSensorName;
				virtualSensor.InputDataPoints = result.inputDatapoints;
				virtualSensor.Conversion = result.conversion;
				virtualSensor.SI = result.SI;
				const datapointIDs: string = Object.entries(JSON.parse(result.inputDatapoints)).map((x) => x[1]).toString();
				virtualSensor.DataPointIDs = datapointIDs;
				this.patchVirtualSensor(virtualSensor);
			}
		});
	}

	/**
	 * createVirtualSensor
	 */
	createVirtualSensor() {
		let dialogData: editVirtualSensorDialogData = {

		}

		const dialogRef = this.dialog.open(EditVirtualSensorComponent, {
			data: dialogData
		});
		dialogRef.afterClosed().pipe(
		).subscribe((result: any) => {
			if (result) {
				const datapointIDs: string = Object.entries(JSON.parse(result.inputDatapoints)).map((x) => x[1]).toString();
				let newVS: VirtualSensor = {
					VirtualSensorID: 0,
					AccountGUID: '',
					OrganisationUID: '',
					InputDataPoints: result.inputDatapoints,
					Conversion: result.conversion,
					VirtualSensorName: result.virtualSensorName,
					AssetIDs: '',
					DataPointIDs: datapointIDs,
					DataPoints: '',
					SI: result.SI
				};
				this.postVirtualSensor(newVS);
			}
		});
	}

	/**
	 * postVirtualSensor
	 * @param virtualSensor virtualSensor 
	 */
	postVirtualSensor(virtualSensor: VirtualSensor) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.virtualSensorsService.postVirtualSensor$(this.AccountGUID, this.OrganisationUID, virtualSensor).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((result) => !!result),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (result) => {
				this.globalFunctions.DisplaySnackBar({
					message: result[0].Result.replace("OK - ", ""),
					duration: 2000,
					action: ""
				});
				this.getVirtualSensors();
			},
			error: (error) => {
				if (error.status != 404) {
					this.globalFunctions.HandleError(error, "Error: this.virtualSensorsService.postVirtualSensor$", false);
				}
			},
		});
	}

	/**
	 * patchVirtualSensor
	 * @param virtualSensor virtualSensor 
	 */
	patchVirtualSensor(virtualSensor: VirtualSensor, overrideMessage?: string) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.virtualSensorsService.patchVirtualSensor$(this.AccountGUID, this.OrganisationUID, virtualSensor.VirtualSensorID, virtualSensor).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((result) => !!result),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (result) => {
				this.globalFunctions.DisplaySnackBar({
					message: overrideMessage ? overrideMessage : result[0].Result.replace("OK - ", ""),
					duration: 2000,
					action: ""
				});
				this.getVirtualSensors();
			},
			error: (error) => {
				if (error.status != 404) {
					this.globalFunctions.HandleError(error, "Error: this.virtualSensorsService.patchVirtualSensor$", false);
				}
			},
		});
	}

	/**
	 * getValueForDP
	 * @param datapoint datapoint 
	 */
	getValueForDP(datapoint: DataPoint, virtualSensor: VirtualSensor): number {
		return parseInt(Object.entries(JSON.parse(virtualSensor.InputDataPoints)).find((item) => item[1] == `${datapoint.DataPointID}`)?.[0] ?? "0");
	}

	/**
	 * getAssetVirtualSensors
	 */
	getAssetVirtualSensors() {
		if (this.asset) {
			this.assetVirtualSensors = this.virtualSensors.filter((vs: VirtualSensor) => vs.AssetIDs.split(",").includes(this.asset.AssetID.toString()));
		}
	}

	/**
	 * removeVirtualSensor
	 * @param virtualSensor virtualSensor 
	 */
	removeVirtualSensor(virtualSensor: VirtualSensor) {
		if (this.asset) {
			virtualSensor.AssetIDs = virtualSensor.AssetIDs.split(",").filter((assetID: string) => assetID != this.asset.AssetID.toString()).toString();
			this.patchVirtualSensor(virtualSensor, "Virtual DataPoint Removed");
		}
	}

	/**
	 * addVirtualSensor
	 */
	addVirtualSensor() {
		if (this.asset) {
			let dialogData: AddVirtualSensorDialogData = {
				virtualSensors: this.virtualSensors,
				asset: this.asset
			}

			const dialogRef = this.dialog.open(AddVirtualSensorComponent, {
				data: dialogData
			});
			dialogRef.afterClosed().pipe(
			).subscribe((result: any) => {
				if (result) {
					result.forEach((vs: VirtualSensor) => {
						let newIDs: string[] = vs.AssetIDs.split(",");
						if (!newIDs.includes(this.asset.AssetID.toString())) {
							newIDs.push(this.asset.AssetID.toString());
						}
						newIDs = newIDs.filter((s: string) => s != '');
						vs.AssetIDs = newIDs.toString();
						this.patchVirtualSensor(vs, "Virtual DataPoint Added");
					});
				}
			});
		}
	}

	/**
	 * requestDeleteVirtualSensor
	 * @param virtualSensor virtualSensor 
	 */
	requestDeleteVirtualSensor(virtualSensor: VirtualSensor) {
		this.globalFunctions.UserConfirm$(`Delete ${virtualSensor.VirtualSensorName}?`).subscribe(async OKResult => {
			if (OKResult) {
				this.deleteVirtualSensor(virtualSensor);
			}
		});
	}

	/**
	 * deleteVirtualSensor
	 * @param virtualSensor virtualSensor 
	 */
	deleteVirtualSensor(virtualSensor: VirtualSensor) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.virtualSensorsService.deleteVirtualSensor$(this.AccountGUID, this.OrganisationUID, virtualSensor.VirtualSensorID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((result) => !!result),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (result) => {
				this.globalFunctions.DisplaySnackBar({
					message: result[0].Result.replace("OK - ", ""),
					duration: 2000,
					action: ""
				});
				this.getVirtualSensors();
			},
			error: (error) => {
				if (error.status != 404) {
					this.globalFunctions.HandleError(error, "Error: this.virtualSensorsService.deleteVirtualSensor$", false);
				}
			},
		});
	}

	// =========================================================================
	// Event table/filter configuration
	// =========================================================================

	/**
	 * onPageChange
	 */
	onPageChange(pag: MatPaginator, event: PageEvent): void {

		if (pag == this._paginatorSensors) {
			this.filterQueryParams.pageSize = pag.pageSize;
			this.filterQueryParams.pageIndex = pag.pageIndex;
		}

		this.applyQueryParameters("All", false)
	}


	// =========================================================================
	// Filtering / query parameters
	// =========================================================================

	/**
	 * Applies the current query parameters to the component's data.
	 *
	 * @param page - The page or data set being queried.
	 * @param reQuery - Determines whether the data should be queried again.
	 */
	applyQueryParameters(page: string = "All", reQuery: boolean = false) {

		if (this.dataSource) {
			// Force MatTableDataSource to re-run filterPredicate
			const currentFilter = this.dataSource.filter;
			this.dataSource.filter = '';
			this.dataSource.filter = currentFilter || this.filterQueryParams.Filter || ' ';
		}

		this.rebuildURL(reQuery);
	}

	/**
	 * Rebuilds the URL using the current query parameter values.
	 *
	 * @param reQuery - Determines whether the data should be queried again
	 * after the URL has been rebuilt.
	 */
	rebuildURL(reQuery: boolean): void {
		if (reQuery) {
			this.router.navigate([], { queryParams: this.filterQueryParams });
		}
		else {
			const urlTree = this.router.createUrlTree([], {
				queryParams: this.filterQueryParams,
				queryParamsHandling: 'merge'
			});

			window.history.replaceState(
				window.history.state,
				'',
				this.router.serializeUrl(urlTree)
			);
		}
	}

	/**
	 * Handles a change to a query parameter.
	 *
	 * @param changed - The name of the query parameter that has changed.
	 */
	onQueryParameterChange(changed: keyof typeof this.filterQueryParams, event: any) {

		if (changed == "Filter") {			
			event.cancel = true;
			const query = (event.target as HTMLInputElement).value.toLowerCase();			
			this.filterQueryParams.Filter = query;
			this.dataSource.filter = query;
		}

		else if (changed == "SortColumn") {
			this.filterQueryParams.SortColumn = event.active;
			this.filterQueryParams.SortDirection = event.direction;
		}

		this.applyQueryParameters("All", false);
	}
}
